/**
 * Bandeau d'installation PWA pour le portail formateur.
 *
 * Cas gérés :
 *  1. Android/Chrome avec `beforeinstallprompt` capturé → bouton "Installer"
 *     qui déclenche le prompt natif.
 *  2. Android/Chrome sans `beforeinstallprompt` (app déjà installée mais
 *     désinstallée localement, heuristiques d'engagement pas encore atteintes,
 *     manifest chargé après le premier paint) → bandeau "Voir comment
 *     installer" qui ouvre une modale avec l'instruction manuelle
 *     "Menu Chrome (⋮) → Installer l'application".
 *  3. iOS Safari → modale "Partager → Sur l'écran d'accueil".
 *
 * Fix 2026-09-02 v2 : Chrome sur Android ne fire pas toujours
 * `beforeinstallprompt` (surtout si l'app a déjà été installée puis
 * désinstallée). Le user pense alors que "ça ne marche pas" alors que
 * l'installation est faisable manuellement via le menu Chrome. On ajoute
 * donc un fallback avec instructions visuelles.
 */
import React, { useEffect, useState } from 'react';
import { Download, Smartphone, X, Info, MoreVertical } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY_TS = 'formateur_install_dismissed_at';

export const FormateurInstallPrompt: React.FC = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [show, setShow] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [help, setHelp] = useState<'ios' | 'android'>('android');

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    const android = /Android/i.test(ua);
    const standalone = (window.navigator as any).standalone === true
      || window.matchMedia('(display-mode: standalone)').matches;

    // Déjà installée → ne rien afficher
    if (standalone) return;

    setIsIos(ios);
    setIsAndroid(android);

    // Respect du dismiss pendant 7 jours
    const dismissedAt = localStorage.getItem(STORAGE_KEY_TS);
    if (dismissedAt) {
      const daysSince = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Fallback : sur mobile, si `beforeinstallprompt` ne s'est pas déclenché
    // au bout de 5s, on affiche quand même le bandeau (l'user pourra suivre
    // les instructions manuelles). Sans ce fallback, l'user Android peut
    // rester bloqué à croire que "l'installation ne fonctionne pas".
    let t: any = null;
    if (ios || android) {
      t = setTimeout(() => setShow(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (t) clearTimeout(t);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      // Prompt natif Chrome/Edge (Android)
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === 'accepted') {
          setShow(false);
        }
      } catch { /* ignore */ }
      setDeferred(null);
      return;
    }
    // Pas de prompt natif dispo → ouvre les instructions manuelles
    setHelp(isIos ? 'ios' : 'android');
    setShowHelp(true);
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY_TS, String(Date.now()));
  };

  if (!show) return null;

  return (
    <>
      {/* Bandeau bas d'écran — bleu-blanc sobre */}
      <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto z-40 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 shadow-2xl rounded-xl overflow-hidden">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 flex items-center justify-center flex-shrink-0">
            <Smartphone size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">
              Installer l'application
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              {deferred
                ? 'Accès rapide depuis l\'écran d\'accueil et notifications.'
                : 'Ouvrez le menu de votre navigateur pour installer l\'app.'}
            </div>
          </div>
          <button
            onClick={install}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1 flex-shrink-0"
          >
            <Download size={12} /> {deferred ? 'Installer' : 'Comment ?'}
          </button>
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
            aria-label="Fermer"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Modale d'instructions */}
      {showHelp && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowHelp(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-2xl max-w-sm w-full overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 dark:border-gray-700 flex items-center gap-2">
              <Info size={16} className="text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                {help === 'android'
                  ? 'Installer sur Android (Chrome)'
                  : 'Installer sur iPhone / iPad'}
              </h3>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700 dark:text-gray-300">
              {help === 'android' ? (
                <>
                  <ol className="space-y-2 list-decimal list-inside">
                    <li>
                      Appuyez sur <MoreVertical size={13} className="inline align-middle text-blue-600" />
                      {' '}en haut à droite (menu Chrome).
                    </li>
                    <li>Choisissez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong>.</li>
                    <li>Confirmez avec <strong>« Installer »</strong>.</li>
                  </ol>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 pt-2 border-t border-slate-100 dark:border-gray-800">
                    L'option n'apparaît que dans Chrome. Firefox et les autres navigateurs Android ne la proposent pas.
                  </div>
                </>
              ) : (
                <>
                  <ol className="space-y-2 list-decimal list-inside">
                    <li>Appuyez sur l'icône <strong>Partager</strong> en bas de Safari (carré avec flèche vers le haut).</li>
                    <li>Faites défiler et choisissez <strong>« Sur l'écran d'accueil »</strong>.</li>
                    <li>Appuyez sur <strong>« Ajouter »</strong> en haut à droite.</li>
                  </ol>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 pt-2 border-t border-slate-100 dark:border-gray-800">
                    Uniquement disponible dans Safari (pas Chrome iOS).
                  </div>
                </>
              )}
            </div>
            <div className="px-5 pb-4">
              <button
                onClick={() => setShowHelp(false)}
                className="w-full py-2 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormateurInstallPrompt;
